import { useState, useEffect } from 'react';
import { api } from '../lib/api';

const LEAD_STATUSES = ['New', 'Contacted', 'Proposal Sent', 'Converted (Won)', 'Lost'];

export default function AgencyLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLeads = () => {
    setLoading(true);
    api.get('/api/agency-crm/leads')
      .then(data => setLeads(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const handleAddLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    const form = e.target;
    const body = {
      clientName: form.clientName.value.trim(),
      companyName: form.companyName.value.trim(),
      mobile: form.mobile.value.trim(),
      email: form.email.value.trim(),
      requirements: form.requirements.value.trim(),
      status: 'New'
    };

    try {
      await api.post('/api/agency-crm/leads', body);
      setShowAddForm(false);
      loadLeads();
    } catch (err) {
      alert('Failed to save lead: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/api/agency-crm/leads/${leadId}`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.del(`/api/agency-crm/leads/${id}`);
      loadLeads();
    } catch (err) {
      alert('Failed to delete lead: ' + err.message);
    }
  };

  if (loading && leads.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading CRM Leads Board…</div>;
  }

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.4rem' }}>CRM Lead Management</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Track prospective client inquiries, proposals, and pipeline conversions</p>
        </div>
        <button onClick={() => setShowAddForm(prev => !prev)} className="cm-btn-emerald">
          {showAddForm ? 'Cancel' : '+ Add New Inquiry Lead'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddLead} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, marginBottom: 24, maxWidth: 580 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 800 }}>New Lead Inquiry</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Contact / Client Name *</label>
              <input name="clientName" type="text" required placeholder="e.g. Rahul Patel" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Company Name</label>
              <input name="companyName" type="text" placeholder="e.g. Patel Organic Products" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Mobile Number *</label>
              <input name="mobile" type="text" required placeholder="e.g. 9876543210" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Email Address</label>
              <input name="email" type="email" placeholder="e.g. rahul@patelorganic.com" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
          </div>
          <div className="cm-field-group" style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Inquiry Requirements</label>
            <textarea name="requirements" placeholder="What services are they looking for? budget? timeline?..." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', height: 80, resize: 'none' }} />
          </div>

          <button type="submit" className="cm-btn-emerald" disabled={saving}>
            {saving ? 'Saving…' : 'Save Lead'}
          </button>
        </form>
      )}

      {/* Leads List Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {leads.map(l => (
          <div key={l.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 850, fontSize: '1.05rem', color: '#0f172a' }}>{l.clientName}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>{l.companyName || 'Individual Lead'}</div>
                </div>
                <button
                  onClick={() => handleDeleteLead(l.id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                  title="Remove Lead"
                >
                  🗑️
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', color: '#475569', marginBottom: 14 }}>
                <div>📞 Mobile: <strong>{l.mobile || '—'}</strong></div>
                <div>📧 Email: <strong>{l.email || '—'}</strong></div>
                <div style={{ background: '#f8fafc', padding: 10, borderRadius: 10, marginTop: 8, fontStyle: 'italic', border: '1px solid #f1f5f9' }}>
                  📝 Requirements: {l.requirements || 'No details provided'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed #f1f5f9', paddingTop: 12, marginTop: 8 }}>
              <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Created: {l.createdAt}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Pipeline:</span>
                <select
                  value={l.status || 'New'}
                  onChange={(e) => handleStatusChange(l.id, e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 800 }}
                >
                  {LEAD_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
